import {
  Accordion,
  Button,
  Card,
  Flex,
  Modal,
  Pill,
  PillsInput,
  Stack,
  Text,
} from '@mantine/core';
import { Group, Provider, Site } from 'interfaces';
import { MRT_Row } from 'mantine-react-table';
import { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';

interface ProviderSiteGroupsProps {
  providerID: string;
  sites: Site[];
  existingGroups: Group[];
  row: MRT_Row<Provider>;
}

export function ProviderGroupComponent({
  providerID,
  sites,
  existingGroups,
  row,
}: ProviderSiteGroupsProps) {
  const initialBySite = useMemo(() => {
    const map: Record<string, Group[]> = {};
    for (const site of sites) map[site.id] = [];
    for (const group of existingGroups) {
      if (!map[group.siteID]) map[group.siteID] = [];
      // avoid duplicate groups within a site (case-insensitive)
      const key = group.name.trim().toLowerCase();
      if (!map[group.siteID].some((x) => x.name.trim().toLowerCase() === key)) {
        map[group.siteID].push(group);
      }
    }
    return map;
  }, [sites, existingGroups]);

  const [groupsBySite, setGroupsBySite] =
    useState<Record<string, Group[]>>(initialBySite);

  useEffect(() => {
    setGroupsBySite(initialBySite);
  }, [initialBySite]);

  useEffect(() => {
    const flat: Group[] = Object.values(groupsBySite).flat();
    row._valuesCache.groups = flat;
  }, [groupsBySite]);

  const addGroup = (name: string, siteID: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setGroupsBySite((prev) => {
      const current = prev[siteID] || [];
      // case-insensitive dedupe by name per site
      const exists = current.some(
        (g) => g.name.trim().toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) return prev;
      const newGroup: Group = {
        id: v4(),
        name: trimmed,
        providerID,
        siteID,
      };
      return { ...prev, [siteID]: [...current, newGroup] };
    });
  };

  const removeById = (siteID: string, id: string) => {
    setGroupsBySite((prev) => ({
      ...prev,
      [siteID]: (prev[siteID] || []).filter((g) => g.id !== id),
    }));
  };

  const [pendingRemoval, setPendingRemoval] = useState<Group | null>(null);

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    removeById(pendingRemoval.siteID, pendingRemoval.id);
    setPendingRemoval(null);
  };

  const cancelRemoval = () => setPendingRemoval(null);

  return (
    <>
      <Accordion
        multiple={false}
        defaultValue="groups"
        variant="separated"
        radius="md"
        w="100%"
        styles={{ item: { borderColor: '#424242' } }}
      >
        <Accordion.Item value="groups">
          <Accordion.Control>Groups</Accordion.Control>
          <Accordion.Panel px={0}>
            <Accordion
              multiple
              variant="separated"
              radius="md"
              w="100%"
              styles={{ item: { borderColor: '#424242' } }}
            >
              {sites.map((site) => (
                <Accordion.Item key={site.id} value={site.id}>
                  <Accordion.Control>{site.name}</Accordion.Control>
                  <Accordion.Panel px={0}>
                    <Card withBorder p="md" radius="md" mt="sm">
                      <Stack gap="md">
                        <PillsInput>
                          <Pill.Group>
                            {(groupsBySite[site.id] || []).map((group) => (
                              <Pill
                                key={group.id}
                                withRemoveButton
                                onRemove={() =>
                                  setPendingRemoval({
                                    siteID: site.id,
                                    id: group.id,
                                    name: group.name,
                                    providerID,
                                  })
                                }
                                data-testid={`group-pill-${group.id}`}
                              >
                                {group.name}
                              </Pill>
                            ))}
                            <PillsInput.Field
                              placeholder={
                                (groupsBySite[site.id] || []).length
                                  ? 'Add another group'
                                  : 'Type a group name and press Enter or blur'
                              }
                              onBlur={(e) => {
                                const el = e.currentTarget as HTMLInputElement;
                                const value = el.value;
                                if (value.trim()) addGroup(value, site.id);
                                el.value = '';
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const el =
                                    e.currentTarget as HTMLInputElement;
                                  const value = el.value;
                                  if (value.trim()) addGroup(value, site.id);
                                  el.value = '';
                                }
                              }}
                              autoComplete="off"
                            />
                          </Pill.Group>
                        </PillsInput>
                      </Stack>
                    </Card>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <Modal
        opened={!!pendingRemoval}
        onClose={cancelRemoval}
        title="Remove group"
        centered
      >
        {pendingRemoval && (
          <Stack gap="md">
            <Text>
              Are you sure you want to remove the group "{pendingRemoval.name}"?
            </Text>
            <Flex gap="sm" align={'center'} justify={'center'}>
              <Button
                variant="outline"
                color="grey"
                c={'red'}
                onClick={confirmRemoval}
                data-testid="confirm-remove-group"
              >
                Remove group
              </Button>
              <Button
                variant="outline"
                onClick={cancelRemoval}
                data-testid="cancel-remove-group"
              >
                Cancel
              </Button>
            </Flex>
          </Stack>
        )}
      </Modal>
    </>
  );
}
